import { Logo } from "@components/Logo";
import { Box, Typography, alpha } from "@mui/material";

/**
 * Full-page loading state — the brand mark "warming up" instead of a
 * generic Material spinner. The pin-1 ember pulses as the agent is
 * about to start signalling, and a thin trace line travels beneath the
 * mark so the surface reads as a board powering on.
 *
 * Used by the route-level Suspense boundary, so the very first paint
 * after JS load is on-brand rather than a default Material spinner.
 */
export function SuspenseLoader() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        height: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          opacity: 0,
          animation: "cf-loader-rise 360ms ease-out 60ms forwards",
          "@keyframes cf-loader-rise": {
            from: { opacity: 0, transform: "translateY(4px) scale(0.96)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
        }}
      >
        <Logo size={48} showWordmark={false} pulseEmber />
      </Box>

      {/* Travelling trace line — same vocabulary as the streaming
          message left rail. Reads as "signal warming up" while the
          route bundle resolves. */}
      <Box
        sx={{
          width: 96,
          height: 2,
          borderRadius: 2,
          overflow: "hidden",
          background: (t) => alpha(t.palette.primary.main, 0.12),
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: (t) =>
              `linear-gradient(90deg, transparent, ${alpha(t.palette.primary.main, 0.7)} 30%, ${alpha(t.palette.warning.main, 0.7)} 50%, ${alpha(t.palette.primary.main, 0.7)} 70%, transparent)`,
            backgroundSize: "240% 100%",
            animation: "cf-loader-trace 1.6s linear infinite",
          },
          "@keyframes cf-loader-trace": {
            "0%": { backgroundPosition: "200% 0%" },
            "100%": { backgroundPosition: "-200% 0%" },
          },
        }}
      />

      <Typography
        sx={{
          fontFamily: (t) => t.fonts.mono,
          fontSize: "0.65rem",
          fontWeight: 500,
          color: "text.disabled",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
        }}
      >
        Booting
      </Typography>
    </Box>
  );
}
